import { useState, useEffect } from 'react';
import { Palette, Save, Link2, Loader2, HardDrive, Inbox, Cloud, CreditCard, BarChart3, Mail, Shield, Key } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminSettings() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [settings, setSettings] = useState({
    primary_color: '#00d4ff',
    accent_color: '#d4a03c',
    background_color: '#0a0e1a',
    text_color: '#ffffff',
    logo_url: '',
    app_name: 'VerbNotes'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/admin/settings`, { headers });
        setSettings(prev => ({ ...prev, ...res.data }));
      } catch (err) {}
      setLoading(false);
    };
    fetch();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, settings, { headers });
      toast.success('Settings saved successfully');
    } catch (err) {
      toast.error('Failed to save settings');
    }
    setSaving(false);
  };

  const colorFields = [
    { key: 'primary_color', label: 'Primary Color (Cyan)' },
    { key: 'accent_color', label: 'Accent Color (Gold)' },
    { key: 'background_color', label: 'Background Color' },
    { key: 'text_color', label: 'Text Color' },
  ];

  return (
    <div>
      <h1 className="font-heading text-3xl text-white mb-8" data-testid="admin-settings-title">App Settings</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Theme Colors */}
        <div className="glass rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <Palette className="text-cyan" size={20} />
            <h2 className="text-lg text-white font-medium">Theme Colors</h2>
          </div>

          <div className="space-y-5">
            {colorFields.map(field => (
              <div key={field.key} className="space-y-2">
                <Label className="text-slate-300">{field.label}</Label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={settings[field.key]}
                    onChange={e => setSettings(prev => ({ ...prev, [field.key]: e.target.value }))}
                    className="w-10 h-10 rounded-lg border border-white/10 bg-transparent cursor-pointer"
                    data-testid={`color-${field.key}`}
                  />
                  <Input
                    value={settings[field.key]}
                    onChange={e => setSettings(prev => ({ ...prev, [field.key]: e.target.value }))}
                    className="bg-navy-900 border-white/10 text-white font-mono text-sm"
                  />
                  <div className="w-10 h-10 rounded-lg border border-white/10 flex-shrink-0" style={{ backgroundColor: settings[field.key] }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* General Settings */}
        <div className="space-y-6">
          <div className="glass rounded-2xl p-6">
            <h2 className="text-lg text-white font-medium mb-5">General</h2>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-300">App Name</Label>
                <Input
                  value={settings.app_name}
                  onChange={e => setSettings(prev => ({ ...prev, app_name: e.target.value }))}
                  className="bg-navy-900 border-white/10 text-white"
                  data-testid="settings-app-name"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Logo URL</Label>
                <Input
                  value={settings.logo_url}
                  onChange={e => setSettings(prev => ({ ...prev, logo_url: e.target.value }))}
                  placeholder="https://..."
                  className="bg-navy-900 border-white/10 text-white"
                  data-testid="settings-logo-url"
                />
              </div>
            </div>
          </div>

          {/* Preview */}
          <div className="glass rounded-2xl p-6">
            <h2 className="text-lg text-white font-medium mb-4">Preview</h2>
            <div className="rounded-xl p-6 border border-white/10" style={{ backgroundColor: settings.background_color }}>
              <h3 className="text-xl font-bold mb-2" style={{ color: settings.primary_color }}>{settings.app_name}</h3>
              <p className="text-sm mb-3" style={{ color: settings.text_color }}>Preview of your theme colors</p>
              <div className="flex gap-2">
                <span className="px-3 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: settings.primary_color, color: settings.background_color }}>Primary</span>
                <span className="px-3 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: settings.accent_color, color: settings.background_color }}>Accent</span>
              </div>
            </div>
          </div>

          <Button onClick={handleSave} disabled={saving} className="w-full bg-cyan text-navy-950 hover:bg-cyan-300 rounded-full py-5" data-testid="save-settings-btn">
            <Save size={16} className="mr-2" />
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </div>

      {/* OAuth Integrations */}
      <OAuthSettings headers={headers} />

      {/* Integration Settings */}
      <IntegrationSettings headers={headers} />
    </div>
  );
}

const serviceInfo = {
  google_drive: { name: 'Google Drive', icon: HardDrive, color: 'text-blue-400' },
  gmail: { name: 'Gmail', icon: Inbox, color: 'text-red-400' },
  dropbox: { name: 'Dropbox', icon: Cloud, color: 'text-sky-400' },
};

function OAuthSettings({ headers }) {
  const [settings, setSettings] = useState({});
  const [secrets, setSecrets] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/oauth-settings`, { headers }).then(r => {
      setSettings(r.data);
      const s = {};
      Object.keys(r.data).forEach(k => { s[k] = { client_id: r.data[k].client_id || '', client_secret: '' }; });
      setSecrets(s);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleSave = async (serviceId) => {
    setSaving(serviceId);
    try {
      await axios.put(`${API}/admin/oauth-settings/${serviceId}`, secrets[serviceId], { headers });
      toast.success(`${serviceInfo[serviceId].name} OAuth settings saved`);
      setSettings(prev => ({ ...prev, [serviceId]: { ...prev[serviceId], client_id: secrets[serviceId].client_id, configured: !!secrets[serviceId].client_id } }));
    } catch { toast.error('Failed to save'); }
    finally { setSaving(null); }
  };

  if (loading) return null;

  return (
    <div className="glass rounded-2xl p-6 mt-6">
      <div className="flex items-center gap-3 mb-6">
        <Link2 className="text-cyan" size={20} />
        <h2 className="text-lg text-white font-medium">OAuth Integrations</h2>
      </div>
      <p className="text-slate-400 text-sm mb-6">Configure OAuth2 credentials for third-party service integrations. Users can connect these from their Settings page.</p>
      <div className="space-y-6">
        {Object.entries(serviceInfo).map(([id, info]) => {
          const Icon = info.icon;
          const isConfigured = settings[id]?.configured;
          return (
            <div key={id} className="bg-navy-950/60 rounded-xl p-5 border border-white/5" data-testid={`oauth-${id}`}>
              <div className="flex items-center gap-3 mb-4">
                <Icon className={info.color} size={20} />
                <span className="text-white font-medium">{info.name}</span>
                {isConfigured && <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400">Configured</span>}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-slate-400 text-xs">Client ID</Label>
                  <Input value={secrets[id]?.client_id || ''} onChange={e => setSecrets(prev => ({ ...prev, [id]: { ...prev[id], client_id: e.target.value } }))}
                    placeholder="Enter Client ID" className="bg-navy-900 border-white/10 text-white text-sm" data-testid={`oauth-${id}-client-id`} />
                </div>
                <div className="space-y-1">
                  <Label className="text-slate-400 text-xs">Client Secret</Label>
                  <Input type="password" value={secrets[id]?.client_secret || ''} onChange={e => setSecrets(prev => ({ ...prev, [id]: { ...prev[id], client_secret: e.target.value } }))}
                    placeholder="Enter Client Secret" className="bg-navy-900 border-white/10 text-white text-sm" data-testid={`oauth-${id}-client-secret`} />
                </div>
              </div>
              <Button onClick={() => handleSave(id)} disabled={saving === id} size="sm" className="mt-3 bg-cyan/10 text-cyan hover:bg-cyan/20" data-testid={`oauth-${id}-save`}>
                {saving === id ? <Loader2 className="animate-spin" size={14} /> : <><Save size={14} className="mr-1" />Save</>}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const INTEGRATION_META = {
  stripe: { label: 'Stripe', icon: CreditCard, color: 'text-indigo-400', fieldLabels: { secret_key: 'Secret Key', publishable_key: 'Publishable Key' } },
  mypos: { label: 'MyPOS', icon: CreditCard, color: 'text-emerald-400', fieldLabels: { client_id: 'Client ID', client_secret: 'Client Secret' } },
  google_analytics: { label: 'Google Analytics', icon: BarChart3, color: 'text-orange-400', fieldLabels: { tracking_id: 'Tracking / Measurement ID' } },
  sendgrid: { label: 'SendGrid', icon: Mail, color: 'text-blue-400', fieldLabels: { api_key: 'API Key', sender_email: 'Sender Email' } },
  auth0: { label: 'Auth0', icon: Shield, color: 'text-red-400', fieldLabels: { domain: 'Domain', client_id: 'Client ID', audience: 'Audience' } },
};

function IntegrationSettings({ headers }) {
  const [integrations, setIntegrations] = useState({});
  const [editValues, setEditValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/integration-settings`, { headers }).then(r => {
      setIntegrations(r.data);
      const edits = {};
      Object.keys(r.data).forEach(k => {
        edits[k] = {};
        (r.data[k].fields || []).forEach(f => { edits[k][f] = ''; });
      });
      setEditValues(edits);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleSave = async (provider) => {
    setSaving(provider);
    try {
      const payload = editValues[provider] || {};
      // Only send non-empty fields
      const filtered = {};
      Object.entries(payload).forEach(([k, v]) => { if (v) filtered[k] = v; });
      if (Object.keys(filtered).length === 0) { toast.error('Please fill in at least one field'); setSaving(null); return; }
      await axios.put(`${API}/admin/integration-settings/${provider}`, filtered, { headers });
      toast.success(`${INTEGRATION_META[provider]?.label || provider} settings saved`);
      // Refresh
      const res = await axios.get(`${API}/admin/integration-settings`, { headers });
      setIntegrations(res.data);
    } catch { toast.error('Failed to save'); }
    finally { setSaving(null); }
  };

  if (loading) return null;

  return (
    <div className="glass rounded-2xl p-6 mt-6" data-testid="integration-settings">
      <div className="flex items-center gap-3 mb-6">
        <Key className="text-cyan" size={20} />
        <h2 className="text-lg text-white font-medium">Integration Settings</h2>
      </div>
      <p className="text-slate-400 text-sm mb-6">Configure API keys and credentials for payment providers, analytics, email, and authentication. Values from .env are used as defaults — settings saved here will override them.</p>
      <div className="space-y-5">
        {Object.entries(INTEGRATION_META).map(([provider, meta]) => {
          const Icon = meta.icon;
          const data = integrations[provider] || {};
          const isConfigured = data.configured;
          const source = data.source;
          const fields = data.fields || Object.keys(meta.fieldLabels);
          const values = data.values || {};

          return (
            <div key={provider} className="bg-navy-950/60 rounded-xl p-5 border border-white/5" data-testid={`integration-${provider}`}>
              <div className="flex items-center gap-3 mb-3">
                <Icon className={meta.color} size={20} />
                <span className="text-white font-medium">{meta.label}</span>
                {isConfigured && (
                  <span className={`text-xs px-2 py-0.5 rounded-full ${source === 'env' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-emerald-500/20 text-emerald-400'}`}>
                    {source === 'env' ? 'From .env' : 'Configured'}
                  </span>
                )}
              </div>

              {/* Current values (masked) */}
              {isConfigured && (
                <div className="flex flex-wrap gap-3 mb-3">
                  {fields.map(f => (
                    <div key={f} className="text-xs">
                      <span className="text-slate-500">{meta.fieldLabels[f] || f}: </span>
                      <span className="text-slate-400 font-mono">{values[f] || '—'}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Edit fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {fields.map(f => {
                  const isSecret = ['secret_key', 'client_secret', 'api_key'].includes(f);
                  return (
                    <div key={f} className="space-y-1">
                      <Label className="text-slate-400 text-xs">{meta.fieldLabels[f] || f}</Label>
                      <Input
                        type={isSecret ? 'password' : 'text'}
                        value={editValues[provider]?.[f] || ''}
                        onChange={e => setEditValues(prev => ({ ...prev, [provider]: { ...prev[provider], [f]: e.target.value } }))}
                        placeholder={isConfigured ? 'Leave empty to keep current' : `Enter ${meta.fieldLabels[f] || f}`}
                        className="bg-navy-900 border-white/10 text-white text-sm"
                        data-testid={`integration-${provider}-${f}`}
                      />
                    </div>
                  );
                })}
              </div>
              <Button onClick={() => handleSave(provider)} disabled={saving === provider} size="sm" className="mt-3 bg-cyan/10 text-cyan hover:bg-cyan/20" data-testid={`integration-${provider}-save`}>
                {saving === provider ? <Loader2 className="animate-spin" size={14} /> : <><Save size={14} className="mr-1" />Save</>}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
